// src/pages/Verifications.tsx
// Page to view all asset verification history
// Created: 2025-02-01

import React, { useState, useEffect } from 'react';
import { 
  RefreshCw, AlertCircle, CheckCircle, MapPin, Calendar,
  User, FileText, Search, Filter
} from 'lucide-react';
import { assetsAPI } from '../services/assetsAPI';
import { AssetVerification, CONDITION_LABELS, AssetCondition } from '../types/asset';
import { logger } from '../utils/logger';

const pageLogger = logger.createServiceLogger('VerificationsPage');

const Verifications: React.FC = () => {
  const [verifications, setVerifications] = useState<AssetVerification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadVerifications();
  }, []);

  const loadVerifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await assetsAPI.getVerifications({ limit: 100 });
      if (response.success && response.data) {
        setVerifications(response.data.verifications || []);
      } else {
        throw new Error(response.error?.message || 'Failed to load verifications');
      }
    } catch (err: any) {
      pageLogger.error('Error loading verifications', { error: err.message });
      setError(err.message || 'Failed to load verifications');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getConditionBadge = (condition?: AssetCondition) => {
    if (!condition) return null;
    
    const colors: Record<AssetCondition, string> = {
      good: 'bg-green-100 text-green-800',
      fair: 'bg-yellow-100 text-yellow-800',
      poor: 'bg-orange-100 text-orange-800',
      damaged: 'bg-red-100 text-red-800'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[condition]}`}>
        {CONDITION_LABELS[condition]}
      </span>
    );
  };

  const filteredVerifications = verifications.filter(v => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      v.assetTag.toLowerCase().includes(search) ||
      v.assetName.toLowerCase().includes(search) ||
      v.verifiedByName.toLowerCase().includes(search) ||
      v.locationVerified.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading verifications...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Verification History</h1>
          <p className="text-sm text-gray-500 mt-1">Record of all asset verifications</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Verifications</p>
              <p className="text-2xl font-bold text-gray-900">{verifications.length}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-500 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">This Month</p>
              <p className="text-2xl font-bold text-blue-600">
                {verifications.filter(v => {
                  const date = new Date(v.verifiedAt);
                  const now = new Date();
                  return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                }).length}
              </p>
            </div>
            <Calendar className="w-10 h-10 text-blue-500 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Location Mismatches</p>
              <p className="text-2xl font-bold text-amber-600">
                {verifications.filter(v => !v.locationMatch).length}
              </p>
            </div>
            <MapPin className="w-10 h-10 text-amber-500 opacity-50" />
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by asset tag, name, verifier, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button 
            onClick={loadVerifications} 
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50"
          >
            <RefreshCw className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Verifications List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Asset</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Verified By</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Condition</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredVerifications.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium">No verifications found</p>
                    <p className="text-sm mt-1">
                      {searchTerm ? 'Try adjusting your search' : 'Verification records will appear here'}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredVerifications.map((verification) => (
                  <tr key={verification.verificationId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(verification.verifiedAt)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-mono text-sm font-medium text-blue-600">{verification.assetTag}</div>
                        <div className="text-sm text-gray-900">{verification.assetName}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <User className="w-4 h-4 mr-1 text-gray-400" />
                        {verification.verifiedByName}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm">
                        <MapPin className={`w-4 h-4 mr-1 ${verification.locationMatch ? 'text-green-500' : 'text-amber-500'}`} />
                        <span className={verification.locationMatch ? 'text-gray-600' : 'text-amber-600'}>
                          {verification.locationVerified}
                        </span>
                      </div>
                      {!verification.locationMatch && verification.locationExpected && (
                        <div className="text-xs text-gray-400 mt-1">
                          Expected: {verification.locationExpected}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {getConditionBadge(verification.condition)}
                    </td>
                    <td className="px-6 py-4">
                      {verification.notes ? (
                        <div className="flex items-start text-sm text-gray-600 max-w-xs">
                          <FileText className="w-4 h-4 mr-1 text-gray-400 flex-shrink-0 mt-0.5" />
                          <span className="truncate">{verification.notes}</span>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Verifications;
